import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SemiconLayout from './SemiconLayout';
import { computeWarpage, DEFAULT_STACK, PRESETS, saveWarpageSnapshot } from './warpageMath';
import { LAYER_NOTES, WARPAGE_NOTES } from './warpageNotes';
import NotesPanel from './NotesPanel';

function drawStack(canvas, layers, bowUm) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  const x0 = 48;
  const L = w - 96;
  const midY = h * 0.52;
  const stackPx = 88;
  const totalT = layers.reduce((s, l) => s + Math.max(1, Number(l.tUm) || 1), 0);
  const amp = Math.sign(bowUm) * Math.min(62, Math.abs(bowUm) * 0.22);

  const yAt = (xi, zFrac) => {
    const camber = 4 * amp * xi * (1 - xi);
    return midY + camber - (zFrac - 0.5) * stackPx;
  };

  ctx.strokeStyle = 'rgba(148,163,184,0.18)';
  ctx.setLineDash([4, 6]);
  ctx.beginPath();
  ctx.moveTo(x0, midY);
  ctx.lineTo(x0 + L, midY);
  ctx.stroke();
  ctx.setLineDash([]);

  let z0 = 0;
  layers.forEach((layer) => {
    const z1 = z0 + Math.max(1, Number(layer.tUm) || 1) / totalT;
    ctx.beginPath();
    for (let s = 0; s <= 48; s += 1) {
      const xi = s / 48;
      const x = x0 + xi * L;
      const y = yAt(xi, z0);
      if (s === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    for (let s = 48; s >= 0; s -= 1) {
      const xi = s / 48;
      ctx.lineTo(x0 + xi * L, yAt(xi, z1));
    }
    ctx.closePath();
    ctx.fillStyle = layer.color || '#64748b';
    ctx.globalAlpha = 0.92;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(7,9,12,0.45)';
    ctx.stroke();
    z0 = z1;
  });

  const tipY = yAt(0.5, 1);
  ctx.strokeStyle = '#67e8f9';
  ctx.beginPath();
  ctx.moveTo(x0 + L / 2, midY);
  ctx.lineTo(x0 + L / 2, tipY);
  ctx.stroke();
  ctx.fillStyle = '#67e8f9';
  ctx.font = '700 12px Inter, sans-serif';
  ctx.fillText(`${bowUm >= 0 ? '+' : ''}${bowUm.toFixed(0)} µm bow`, x0 + L / 2 + 8, (midY + tipY) / 2);
  ctx.fillStyle = '#94a3b8';
  ctx.font = '11px Inter, sans-serif';
  ctx.fillText('leadframe / mold side', x0, h - 18);
  ctx.fillText('die side', x0, 28);
}

const WarpageBench = () => {
  const [layers, setLayers] = useState(DEFAULT_STACK.map((l) => ({ ...l })));
  const [deltaT, setDeltaT] = useState(-155);
  const [lengthMm, setLengthMm] = useState(100);
  const canvasRef = useRef(null);
  const result = useMemo(() => computeWarpage(layers, deltaT, lengthMm), [layers, deltaT, lengthMm]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = Math.max(640, rect.width * 2);
      canvas.height = 640;
      drawStack(canvas, layers, result.bowUm);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [layers, result.bowUm]);

  useEffect(() => {
    saveWarpageSnapshot({
      bowUm: result.bowUm,
      dieStressMpa: result.dieStressMpa,
      spec: result.spec,
      deltaT,
      lengthMm,
      stack: layers.map((l) => `${l.name} ${l.tUm}µm / ${l.ctePpm} ppm/K`),
    });
  }, [result.bowUm, deltaT, lengthMm, layers]);

  const setLayer = (i, key, value) => {
    setLayers((prev) => prev.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));
  };

  const applyPreset = (key) => {
    const p = PRESETS[key];
    setLayers(p.layers.map((l) => ({ ...l })));
    setDeltaT(p.deltaT);
  };

  return (
    <SemiconLayout
      kicker="Station 1 · cool-down after mold"
      title="The strip actually bows"
      lead="A QFN-like stack leaving mold cure. CTE mismatch is a moment. The beam cups. This is not a number in a table — watch the die side lift."
    >
      <div className="sm-presets">
        {Object.entries(PRESETS).map(([key, p]) => (
          <button key={key} type="button" className="sm-chip" onClick={() => applyPreset(key)}>{p.label}</button>
        ))}
      </div>
      <div className="sm-split">
        <div className="sm-controls">
          {layers.map((l, i) => (
            <div key={l.name} className="sm-layer">
              <div className="sm-layer-h">
                <span className="sm-swatch" style={{ background: l.color }} />
                {l.name}
              </div>
              <div className="sm-row">
                <label className="sm-field">t {Number(l.tUm)} µm
                  <input type="range" min="10" max="900" value={l.tUm} onChange={(e) => setLayer(i, 'tUm', +e.target.value)} />
                </label>
                <label className="sm-field">CTE {Number(l.ctePpm).toFixed(1)}
                  <input type="range" min="1" max="90" step="0.1" value={l.ctePpm} onChange={(e) => setLayer(i, 'ctePpm', +e.target.value)} />
                </label>
                <label className="sm-field">E {Number(l.eGpa)} GPa
                  <input type="range" min="0.4" max="200" step="0.1" value={l.eGpa} onChange={(e) => setLayer(i, 'eGpa', +e.target.value)} />
                </label>
              </div>
            </div>
          ))}
          <label className="sm-field">Cool-down ΔT {deltaT} K
            <input type="range" min="-220" max="20" value={deltaT} onChange={(e) => setDeltaT(+e.target.value)} />
          </label>
          <label className="sm-field">Strip {lengthMm} mm
            <input type="range" min="40" max="200" value={lengthMm} onChange={(e) => setLengthMm(+e.target.value)} />
          </label>
        </div>
        <div>
          <div className="sm-stage">
            <span className="sm-stage-tag">Side view · display exaggerated</span>
            <canvas ref={canvasRef} />
          </div>
          <div className="sm-readout">
            <p className="sm-eq">δ = κ L² / 8 · σ_die ≈ E κ (z_die − z_n)</p>
            <strong>{result.bowUm >= 0 ? '+' : ''}{result.bowUm.toFixed(1)} µm</strong>
            <span className={`sm-spec sm-spec--${result.spec}`}>{result.spec}</span>
            <p className="sm-note">{result.specNote}</p>
            <div className="sm-kpi">
              <div><span>Radius of curvature</span><b>{Number.isFinite(result.radiusMm) ? `${result.radiusMm.toFixed(0)} mm` : '∞'}</b></div>
              <div><span>Die bending (order)</span><b>{result.dieStressMpa.toFixed(0)} MPa</b></div>
              <div><span>Neutral axis</span><b>{result.zBarUm.toFixed(0)} µm</b></div>
            </div>
            <p className="sm-note">
              {result.thicknessUm.toFixed(0)} µm stack · cups toward the {result.concaveToward}.
              Die stress is a beam estimate, not a FEA peak at the die corner — corners will be worse.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem', flexWrap: 'wrap' }}>
              <Link to="/virtuallabs/semicon-mech/peel-shear" className="sm-btn">Peel the joint →</Link>
            </div>
          </div>
        </div>
      </div>
      <NotesPanel title="Line notes — warpage" items={WARPAGE_NOTES} />
      <NotesPanel
        title="What each layer is for"
        items={layers.map((l) => ({
          heading: LAYER_NOTES[l.name]?.heading || l.name,
          body: [LAYER_NOTES[l.name]?.body || ''],
        }))}
      />
    </SemiconLayout>
  );
};

export default WarpageBench;
